import type { CvtSbrResponseData, SBRResponseData } from '@packages/models/api/cvt';

import { isNoResultErrorType } from '@packages/contexts/qmr-cvt-diagnostic';
import { colors } from '@packages/core/styles';
import { Icon, TextBadge } from '@packages/ui/shared';
import { TextBadgeWithCount } from '@web/components/text-badge-with-count';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    badgesToolTip: {
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '16px',
            maxWidth: 262,
            padding: '4px 8px',
            textAlign: 'left',
        },
    },
});

const textBadgeStyle = { marginLeft: 5, paddingRight: 6 };

type DiagnosticResultBadgeProps = {
    diagnosticId?: string;
    sbrResponseData?: CvtSbrResponseData | SBRResponseData;
};

/**
 * The badge for a CVT diagnostic test's result, drawn as the CVT page draws it.
 *
 * Renders "No Result" in green for a test that found no abnormalities. For a test with symptoms, it renders the first
 * symptom title in yellow. When there are more, a `+n` badge follows, and hovering either badge lists every title.
 */
export function DiagnosticResultBadge({ diagnosticId, sbrResponseData }: DiagnosticResultBadgeProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    if (isNoResultErrorType(sbrResponseData)) {
        return (
            <TextBadge
                icon={<Icon color="greenOne" name="check-circle-hollow" size={18} />}
                style={textBadgeStyle}
                typographyProps={{ numberOfLines: 1 }}
                variant="green"
            >
                {t('qmr:sections.failCode.cvtDiagnostic.noResult', 'No Result')}
            </TextBadge>
        );
    }

    return (
        <TextBadgeWithCount
            items={toSymptoms(sbrResponseData)}
            tooltipClassName={classes.badgesToolTip}
            tooltipId={`cvt-diagnostic-symptoms-${diagnosticId}`}
            variant="yellow"
        />
    );
}

function toSymptoms(sbrResponseData?: CvtSbrResponseData | SBRResponseData): string[] {
    if (!sbrResponseData || !('symptomTitle' in sbrResponseData)) {
        return [];
    }

    return sbrResponseData.symptomTitle ?? [];
}
